import React from "react";
import { useTranslation } from "@1f/react-sdk";
import Stack from "@vapor/react-material/Stack";
import Card from "@vapor/react-material/Card";
import CardContent from "@vapor/react-material/CardContent";
import CardActions from "@vapor/react-material/CardActions";
import Box from "@vapor/react-material/Box";
import IconButton from "@vapor/react-material/IconButton";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";
import { NoData } from "../../../components/NoData";
import { CustomTooltip } from "../../../components/CustomTooltip";

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  fill,
}) => {
  const radius = innerRadius + outerRadius * 0.8;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={fill}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(2)}%`}
    </text>
  );
};

const ChartCard = ({
  title,
  data,
  counterLabel,
  counterValue,
  onClick,
}) => (
  <Card>
    <CardContent sx={{ p: 0, pb: "0 !important" }}>
      <Stack direction="column" sx={{ p: 4 }}>
        <ExtendedTypography
          variant="titleSmall"
          color="primary.main"
          sx={{ mb: 1 }}
        >
          {title}
        </ExtendedTypography>

        <ResponsiveContainer width="100%" height={200} minWidth={0}>
          {data.length > 0 ? (
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={50}
                fill="#5676D4"
                label={renderCustomizedLabel}
                isAnimationActive={true}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          ) : (
            <NoData size="small" />
          )}
        </ResponsiveContainer>
      </Stack>
    </CardContent>
    <CardActions sx={{ p: 0 }}>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          p: 1,
          pl: { xs: 2, md: 4 },
          borderTop: "solid 1px #cccccc",
        }}
        alignItems="center"
      >
        <Stack direction="column">
          <ExtendedTypography variant="bodySmall" color="text.secondary">
            {counterLabel}
          </ExtendedTypography>
          <ExtendedTypography
            variant="bodySmall"
            color="text.secondary"
            sx={{ fontWeight: "bold" }}
          >
            {counterValue}
          </ExtendedTypography>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton color="primary" onClick={onClick}>
          <ArrowForwardIcon />
        </IconButton>
      </Stack>
    </CardActions>
  </Card>
);

export const DashboardCharts = ({
  user,
  statsFascicoliData,
  statsPfPgData,
  statsPrestData,
  statsRischiData,
  entityCounters,
  onGoToFascicoloHome,
  onGoToAnagraficaHome,
  onGoToPrestazioneHome,
  onGoToValutazioneHome,
}) => {
  const { t } = useTranslation();
  const showPerformanceAndEffectiveRisk = user && user.tenantTipoId !== 3;
  const cards = [
    {
      title: t("dashboard.georisk"),
      data: statsFascicoliData,
      counterLabel: t("dashboard.regTot"),
      counterValue: entityCounters.fascicoli,
      onClick: onGoToFascicoloHome,
    },
    {
      title: t("dashboard.exposedPeopleTotal"),
      data: statsPfPgData,
      counterLabel: t("dashboard.pfTotal"),
      counterValue: entityCounters.anagrafiche,
      onClick: onGoToAnagraficaHome,
    },
  ];

  if (showPerformanceAndEffectiveRisk) {
    cards.push(
      {
        title: t("dashboard.perfRisk"),
        data: statsPrestData,
        counterLabel: t("dashboard.perfTotal"),
        counterValue: entityCounters.prestazioni,
        onClick: onGoToPrestazioneHome,
      },
      {
        title: t("dashboard.riskeff"),
        data: statsRischiData,
        counterLabel: t("dashboard.evalTot"),
        counterValue: entityCounters.valutazioni,
        onClick: onGoToValutazioneHome,
      }
    );
  }

  const gridTemplateColumns = showPerformanceAndEffectiveRisk
    ? "repeat(4, minmax(0, 1fr))"
    : "repeat(2, minmax(0, 1fr))";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns,
        gap: 2,
        mt: { xs: 2, md: 3 },
        minWidth: 0,
      }}
    >
      {cards.map((card) => (
        <ChartCard
          key={card.title}
          title={card.title}
          data={card.data}
          counterLabel={card.counterLabel}
          counterValue={card.counterValue}
          onClick={card.onClick}
        />
      ))}
    </Box>
  );
};
